import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import { ReactComponent as ChatBubbleIcon } from "assets/mail-chat-bubble-square-warning.svg";
import BasicTable, { StyledLink } from "components/common/Table";
import Typography from "components/common/Typography";
import { useTranslate } from "hooks/useTranslate";
import { useEffect } from "react";
import { useParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { tokens } from "styles/Tokens";
import { messages } from "utils/CommonConstants";
import { getMoreValues, getTableColor, getUserTypeTag } from "utils/CommonFunctions";
import { setOwners } from "../../AboveProperty/AboveProperty.slice";
import FilterButtons from "../../AboveProperty/FilterButtons";
import { fetchMembers } from "../IndividualProperty.slice";
import { headers } from "./TableHeadersConfig";

interface Props {}

function Members(props: Props) {
  const params = useParams();
  const dispatch = useAppDispatch();
  const translate = useTranslate();
  const { abovePropertyLoader, owners } = useAppSelector((state) => state.aboveProperty);
  const members = useAppSelector((state) => state.individualProperty.members);
  const filters = useAppSelector((state: any) => state.aboveProperty.filters.individualPropertyFilters.memberFilters);
  const { searchText, role, roleCategory, engagements, includeAboveProperty } = filters;

  useEffect(() => {
    dispatch(
      fetchMembers({
        innCode: params.inncode,
        searchedText: searchText,
        roles: role,
        roleCategory,
        engagements,
        excludeAbovePropertyUsers: !includeAboveProperty
      })
    ).then((res: any) => {
      if (!owners.length) {
        dispatch(setOwners(res?.payload?.members));
      }
    });
  }, [filters]);

  return (
    <>
      <Stack sx={{ mt: "44px" }}>
        <Typography
          variant="h4"
          showBold
          color={tokens.Text.General.PrimaryHeadline}
          sx={{
            mb: "8px"
          }}
        >
          Members
        </Typography>
        <Typography variant="body2" color={tokens.Text.General.Secondary}>
          {members.length} Members
        </Typography>
      </Stack>
      <Box
        sx={{
          backgroundColor: tokens.Background.General.Primary,
          borderRadius: "12px",
          overflow: "hidden",
          mt: "32px"
        }}
      >
        <FilterButtons
          parentView="individualPropertyFilters"
          filterType="memberFilters"
          searchPredefinedValue={searchText}
          isUserTypeToggleVisible
          toggleProp={{
            toggleLabelOn: translate("common.includeAboveProperty"),
            toggleLabelOff: translate("common.excludeAboveProperty"),
            isLabelLeftSide: true,
            isToggleEnabled: includeAboveProperty
          }}
          searchBarPlaceHolder={"Search by Member"}
          isSearchBarVisible
          isRoleVisible
          isRoleCategoryVisible
          isEngagementVisible
        />

        {!abovePropertyLoader && (
          <BasicTable
            isTablePaginated={false}
            noDataIcon={<ChatBubbleIcon />}
            totalRecords={members?.length}
            noDataMessage={messages.noAbovePropertiesMembers}
            sx={{ maxHeight: "550px" }}
            data={{
              headers,
              rows: members?.map((member: any) => {
                return {
                  data: [
                    {
                      content: (
                        <StyledLink to={`/userview/${member?.planAccessTo}?inncode=${params?.inncode}`}>
                          {member.displayName}
                        </StyledLink>
                      ),
                      showBold: true
                    },
                    {
                      content: getMoreValues(member?.roles),
                      tooltip: {
                        title: "Role",
                        list: member?.roles?.map((role: any) => {
                          return { name: role };
                        })
                      }
                    },
                    { content: member?.roleCategory },
                    {
                      content: getUserTypeTag(member?.lobbyUserType)
                    },
                    {
                      content: member.totalPlansCount,
                      tooltip: {
                        title: "Plans",
                        list: [
                          { name: "New", value: member.newPlansCount },
                          { name: "In Progress", value: member.inProgressPlansCount },
                          { name: "Completed w/i 90 days", value: member.completedPlansCount },
                          { name: "Overdue", value: member.overduePlansCount }
                        ]
                      }
                    },
                    {
                      content: member.totalCardsCount,
                      tooltip: {
                        title: "Assigned Actions",
                        list: [
                          { name: "New", value: member.newAssignedCardsCount },
                          { name: "In Progress", value: member.inProgressAssignedCardsCount },
                          { name: "Completed w/i 90 days", value: member.completedAssignedCardsCount },
                          { name: "Overdue", value: member.overdueCardsCount }
                        ]
                      }
                    },
                    {
                      content: member.engagement,
                      color: getTableColor(member.engagement)
                    }
                  ]
                };
              })
            }}
          />
        )}
      </Box>
    </>
  );
}

export default Members;
